import { useState } from 'react';
import { TaxResult } from '../../../utils/TaxEngine';

interface FAQSectionProps {
  locationName: string;
  salary: number;
  taxResult: TaxResult;
  currencySymbol: string;
  nearbyCities: Array<{name: string, slug: string, path: string}>;
}

const FAQSection = ({ locationName, salary, taxResult, currencySymbol, nearbyCities }: FAQSectionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: `How much tax do I pay in ${locationName} on ${currencySymbol}${salary.toLocaleString()}?`,
      answer: `On a salary of ${currencySymbol}${salary.toLocaleString()} in ${locationName}, you would pay approximately ${currencySymbol}${Math.round(taxResult.totalTax).toLocaleString()} in total taxes, which represents an effective tax rate of ${(taxResult.effectiveRate * 100).toFixed(2)}%. This includes all applicable federal, state, and local taxes, as well as social insurance contributions.`
    },
    {
      question: `What is the take-home pay for ${locationName} residents?`,
      answer: `For a gross salary of ${currencySymbol}${salary.toLocaleString()} in ${locationName}, your take-home pay would be approximately ${currencySymbol}${Math.round(taxResult.netSalary).toLocaleString()} annually. This calculation accounts for all mandatory tax deductions including federal income tax, state income tax, local taxes, and payroll taxes like Social Security and Medicare.`
    },
    {
      question: `Is ${locationName} tax higher than ${nearbyCities[0]?.name || 'nearby cities'}?`,
      answer: `Tax rates vary significantly between locations due to differences in state and local tax policies. ${locationName} has its own unique tax structure that includes specific state and local tax rates. To compare accurately, use our calculator for ${nearbyCities[0]?.name || 'other locations'} and see the side-by-side differences in take-home pay.`
    },
    {
      question: 'How accurate is this salary calculator?',
      answer: 'Our calculator uses the latest tax brackets and rates for 2026, including federal, state, and local tax information. While we strive for accuracy, individual circumstances such as deductions, credits, and filing status can affect your actual tax liability. This calculator provides estimates for planning purposes and should not replace professional tax advice.'
    },
    {
      question: 'What factors affect my take-home pay?',
      answer: 'Several factors influence your net salary including your gross income, filing status (single, married, etc.), number of dependents, pre-tax deductions (401k, HSA), state and local tax rates, and additional withholdings. Our advanced settings allow you to customize these parameters for a more accurate calculation.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Frequently Asked Questions
        </h2>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <span className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </span>
                <i
                  className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform`}
                ></i>
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5 border-l-4 border-blue-500 bg-blue-50 animate-fadeIn">
                  <p className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;